import React, { useState, useEffect } from 'react';
import {
  IconButton,
  Badge,
  Menu,
  MenuItem,
  Box,
  Typography,
  Button,
  CircularProgress,
  Chip,
  useTheme,
  Tooltip,
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  CheckCircle,
  Cancel,
  Info,
  Warning,
  Circle,
  VolumeUp,
  VolumeOff,
  Mail,
} from '@mui/icons-material';
import { api } from '../../lib/api';
import { usePatientDetails } from '../../contexts/PatientDetailsContext';
import { Notification } from '../../types';
import { formatDistanceToNow } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { useWebSocket } from '../../hooks/useWebSocket';
import toast from 'react-hot-toast';
import { safeConsole } from '../../lib/piiProtection';
import { safeInternalPath } from '../../lib/safeNavigation';

const NotificationBell: React.FC = () => {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const navigate = useNavigate();
  const { openPatient } = usePatientDetails();
  const { isConnected, subscribe } = useWebSocket();

  const open = Boolean(anchorEl);

  // Create notification sound
  const playNotificationSound = () => {
    if (soundEnabled) {
      try {
        const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();
        
        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);
        
        oscillator.frequency.value = 800;
        oscillator.type = 'sine';
        
        gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);
        
        oscillator.start(audioContext.currentTime);
        oscillator.stop(audioContext.currentTime + 0.3);
      } catch (err) {
        console.error('Failed to play notification sound:', err);
      }
    }
  };

  // Load initial unread count on mount
  useEffect(() => {
    loadUnreadCount();
  }, []);

  // Subscribe to WebSocket messages when connected
  useEffect(() => {
    if (isConnected) {
      
      // 1. Listen for new messages
      const unsubscribeMessages = subscribe('/user/queue/messages', (message) => {
        const data = typeof message === 'string' ? JSON.parse(message) : message;
        
        // Treat new messages as notifications for the bell
        setUnreadCount(prev => prev + 1);
        
        // Format for the list
        const msgNotification: Notification = {
          id: data.id,
          title: `New message from ${data.senderName || 'Staff'}`,
          message: data.subject || data.content,
          type: 'CLINIC_MESSAGE',
          priority: 'NORMAL',
          isRead: false,
          createdAt: data.createdAt,
          relatedEntityId: data.id,
          relatedEntityType: 'MESSAGE'
        };

        setNotifications(prev => [msgNotification, ...prev]);
        playNotificationSound();
        toast.success(`Message: ${msgNotification.title}`, {
           duration: 5000,
           position: 'bottom-right',
           icon: '✉️'
        });
      });

      // 2. Listen for system notifications (Prescriptions, Labs, Transfers, etc.)
      const unsubscribeNotifications = subscribe('/user/queue/notifications', (message) => {
        const data = typeof message === 'string' ? JSON.parse(message) : message;

        setUnreadCount(prev => prev + 1);
        
        const systemNotification: Notification = {
          id: data.id || Math.random().toString(),
          title: data.title || 'System Alert',
          message: data.message || '',
          type: data.type || 'SYSTEM',
          priority: data.priority || 'NORMAL',
          isRead: false,
          createdAt: data.createdAt || new Date().toISOString(),
          actionUrl: data.actionUrl,
          relatedEntityId: data.relatedEntityId,
          relatedEntityType: data.relatedEntityType
        };

        setNotifications(prev => [systemNotification, ...prev]);
        playNotificationSound();
        
        toast((_t) => (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{ fontSize: '1.2rem' }}>{getIconEmoji(systemNotification.type)}</Box>
            <Box>
              <Typography variant="body2" fontWeight="800">{systemNotification.title}</Typography>
              <Typography variant="caption" color="text.secondary">{systemNotification.message}</Typography>
            </Box>
          </Box>
        ), {
          duration: 6000,
          position: 'bottom-right',
          style: {
            borderRadius: '12px',
            background: '#fff',
            color: '#333',
          },
        });
      });

      return () => {
        unsubscribeMessages();
        unsubscribeNotifications();
      };
    }
  }, [isConnected, subscribe]);

  const getIconEmoji = (type: string) => {
    if (!type) return '🔔';
    const t = type.toUpperCase();
    if (t.includes('PRESCRIPTION')) return '💊';
    if (t.includes('LAB')) return '🧪';
    if (t.includes('APPOINTMENT')) return '📅';
    if (t.includes('PAYMENT')) return '💰';
    if (t.includes('TRANSFER') || t.includes('REFERRAL')) return '🔄';
    return '🔔';
  };

  const loadUnreadCount = async () => {
    try {
      const [sysCount, messages] = await Promise.allSettled([
        api.notifications.getUnreadCount(),
        api.messages.getInbox(),
      ]);
      const sys = sysCount.status === 'fulfilled' ? (sysCount.value as { count: number }).count : 0;
      const msgs = messages.status === 'fulfilled' ? messages.value.filter((m: any) => !m.isRead).length : 0;
      setUnreadCount(sys + msgs);
    } catch (err) {
      safeConsole.error('NotificationBell: Failed to load unread count:', err);
    }
  };

  const loadNotifications = async () => {
    setLoading(true);
    try {
      const [sysResult, msgResult] = await Promise.allSettled([
        api.notifications.getAll(20),
        api.messages.getInbox(),
      ]);

      const sysNotifs: Notification[] = sysResult.status === 'fulfilled'
        ? (sysResult.value as Notification[])
        : [];

      const msgNotifs: Notification[] = msgResult.status === 'fulfilled'
        ? msgResult.value.slice(0, 10).map((m: any) => ({
            id: m.id,
            title: m.senderName || 'Staff Message',
            message: m.subject || m.content,
            type: 'CLINIC_MESSAGE' as const,
            priority: 'NORMAL' as const,
            isRead: m.isRead ?? false,
            read: m.isRead ?? false,
            userId: m.senderId ?? '',
            createdAt: m.createdAt,
            relatedEntityId: m.id,
            relatedEntityType: 'MESSAGE',
          }) as unknown as Notification)
        : [];

      // Merge and sort newest-first; cap at 20
      const merged = [...sysNotifs, ...msgNotifs]
        .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
        .slice(0, 20);

      setNotifications(merged);
    } catch (err) {
      safeConsole.error('NotificationBell: Failed to load notifications:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    loadNotifications();
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleNotificationClick = async (notification: Notification) => {
    handleClose();

    if (!notification.isRead) {
      try {
        if (notification.relatedEntityType === 'MESSAGE') {
          await api.messages.markAsRead(notification.id);
        } else {
          await api.notifications.markAsRead(notification.id);
        }
        setUnreadCount(prev => Math.max(0, prev - 1));
        setNotifications(prev =>
          prev.map(n => (n.id === notification.id ? { ...n, isRead: true } : n))
        );
      } catch (err) {
        safeConsole.error('NotificationBell: Failed to mark notification as read:', err);
      }
    }

    // Lab-result notifications deep-link to the patient — open their record window (labs visible)
    // instead of dumping the doctor on the generic patients list.
    const openPatientMatch = notification.actionUrl?.match(/openPatient=([^&]+)/);
    if (openPatientMatch) {
      const patientId = decodeURIComponent(openPatientMatch[1]);
      try {
        const res = await api.patients.getById(patientId);
        // Open straight to the labs (diagnostics) accordion for lab-result notifications.
        if (res.data) { openPatient(patientId, res.data, undefined, 'readings'); return; }
      } catch {
        // fall through to plain navigation
      }
      navigate('/patients');
      return;
    }

    // Navigate to actionUrl for system notifications (e.g. /transfers), or inbox for messages
    if (notification.actionUrl && notification.relatedEntityType !== 'MESSAGE') {
      // actionUrl arrives in an API payload, so it is untrusted input to the router. Anything that
      // is not an unambiguously internal path falls back to the notification itself rather than
      // sending the user off-site. See lib/safeNavigation.
      navigate(safeInternalPath(notification.actionUrl, `/notifications?id=${notification.id}`));
    } else {
      navigate(`/notifications?id=${notification.id}`);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await api.notifications.markAllAsRead();
      setUnreadCount(0);
      setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
    } catch (err) {
      safeConsole.error('NotificationBell: Failed to mark all as read:', err);
    }
    handleClose();
  };

  const getIcon = (type: string) => {
    if (!type) return <Info fontSize="small" color="action" />;

    const typeUpper = type.toUpperCase();
    if (typeUpper.includes('MESSAGE')) return <Mail fontSize="small" color="primary" />;
    if (typeUpper.includes('ACCEPTED')) return <CheckCircle fontSize="small" color="success" />;
    if (typeUpper.includes('REJECTED')) return <Cancel fontSize="small" color="error" />;
    if (typeUpper.includes('READY')) return <Info fontSize="small" color="info" />;
    if (typeUpper.includes('COLLECTED')) return <CheckCircle fontSize="small" color="success" />;
    if (typeUpper.includes('ALERT') || typeUpper.includes('URGENT')) return <Warning fontSize="small" color="warning" />;
    if (typeUpper.includes('TRANSFER') || typeUpper.includes('REFERRAL')) return <Info fontSize="small" color="warning" />;

    return <Info fontSize="small" color="action" />;
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'URGENT': return 'error';
      case 'HIGH': return 'warning';
      case 'NORMAL': return 'info';
      case 'LOW': return 'default';
      default: return 'default';
    }
  };

  return (
    <>
      <IconButton
        color="inherit"
        onClick={handleClick}
        aria-label={`${unreadCount} unread notifications`}
      >
        <Badge badgeContent={unreadCount} color="error">
          <NotificationsIcon />
        </Badge>
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        PaperProps={{
          elevation: 4,
          sx: {
            width: 420,
            maxHeight: 520,
            borderRadius: 3,
            mt: 1.5,
            overflow: 'hidden',
            border: `1px solid ${theme.palette.divider}`,
            '& .MuiList-root': { padding: 0 }
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <Box sx={{ 
          px: 2.5, 
          py: 2, 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          bgcolor: 'rgba(0,0,0,0.02)',
          borderBottom: `1px solid ${theme.palette.divider}`
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Typography variant="h6" fontWeight="800" sx={{ letterSpacing: '-0.5px' }}>Notifications</Typography>
            {unreadCount > 0 && (
              <Chip
                label={`${unreadCount} New`}
                size="small"
                color="primary"
                sx={{ height: 20, fontSize: '0.7rem', fontWeight: 700, borderRadius: 1 }}
              />
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <Tooltip title={soundEnabled ? 'Mute' : 'Unmute'}>
              <IconButton size="small" onClick={() => setSoundEnabled(!soundEnabled)}>
                {soundEnabled ? <VolumeUp fontSize="small" /> : <VolumeOff fontSize="small" />}
              </IconButton>
            </Tooltip>
            {unreadCount > 0 && (
              <Button 
                size="small" 
                onClick={handleMarkAllRead}
                sx={{ fontSize: '0.75rem', fontWeight: 700 }}
              >
                Clear all
              </Button>
            )}
          </Box>
        </Box>

        <Box sx={{ maxHeight: 400, overflowY: 'auto' }}>
          {loading ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 6, gap: 1 }}>
              <CircularProgress size={24} thickness={5} />
              <Typography variant="caption" color="text.secondary">Fetching updates...</Typography>
            </Box>
          ) : notifications.length === 0 ? (
            <Box sx={{ px: 4, py: 8, textAlign: 'center' }}>
              <Box sx={{ opacity: 0.1, mb: 1 }}>
                <NotificationsIcon sx={{ fontSize: 48 }} />
              </Box>
              <Typography variant="subtitle2" fontWeight="700">All caught up!</Typography>
              <Typography variant="caption" color="text.secondary">
                No new alerts or messages at the moment.
              </Typography>
            </Box>
          ) : (
            <>
              {notifications.map((notification) => (
                <MenuItem
                  key={notification.id}
                  onClick={() => handleNotificationClick(notification)}
                  sx={{
                    py: 2,
                    px: 2.5,
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    backgroundColor: notification.isRead ? 'transparent' : 'rgba(99, 102, 241, 0.04)',
                    '&:hover': {
                      backgroundColor: notification.isRead ? 'action.hover' : 'rgba(99, 102, 241, 0.08)',
                    },
                    alignItems: 'flex-start',
                    flexDirection: 'column',
                    whiteSpace: 'normal'
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', width: '100%', gap: 2 }}>
                    <Box sx={{ mt: 0.5 }}>
                      {getIcon(notification.type)}
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
                        <Typography 
                          variant="subtitle2" 
                          sx={{ 
                            fontWeight: notification.isRead ? 500 : 800,
                            color: notification.isRead ? 'text.secondary' : 'text.primary',
                            lineHeight: 1.3
                          }}
                        >
                          {notification.title}
                        </Typography>
                        {!notification.isRead && (
                          <Circle sx={{ fontSize: 8, color: 'primary.main', ml: 1, mt: 0.5 }} />
                        )}
                      </Box>
                      <Typography 
                        variant="body2" 
                        color="text.secondary" 
                        sx={{ 
                          mb: 1, 
                          fontSize: '0.825rem',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden'
                        }}
                      >
                        {notification.message}
                      </Typography>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="caption" sx={{ color: 'text.disabled', fontWeight: 500 }}>
                          {notification.createdAt ? (() => {
                            try {
                              const date = new Date(notification.createdAt);
                              if (isNaN(date.getTime())) return 'Just now';
                              return formatDistanceToNow(date, { addSuffix: true });
                            } catch (e) {
                              return 'Just now';
                            }
                          })() : 'Just now'}
                        </Typography>
                        
                        {notification.priority !== 'NORMAL' && (
                          <Chip
                            label={notification.priority}
                            size="small"
                            color={getPriorityColor(notification.priority) as any}
                            sx={{ height: 16, fontSize: '0.6rem', fontWeight: 800, borderRadius: 0.5 }}
                          />
                        )}
                      </Box>
                    </Box>
                  </Box>
                </MenuItem>
              ))}
            </>
          )}
        </Box>

        <Box sx={{ p: 1, bgcolor: 'rgba(0,0,0,0.01)', borderTop: `1px solid ${theme.palette.divider}` }}>
          <Button 
            fullWidth
            size="small" 
            variant="text" 
            color="primary"
            onClick={() => {
              handleClose();
              navigate('/notifications');
            }}
            sx={{ fontWeight: 700, borderRadius: 2 }}
          >
            Open Integrated Inbox
          </Button>
        </Box>
      </Menu>
    </>
  );
};

export default NotificationBell;
